<template>
  <div
      class="w-full text-slate-100"
      dir="rtl"
  >
    <div class="w-full max-w-7xl mx-auto space-y-5 px-3 sm:px-4 lg:px-6 py-5">
      <!-- Header -->
      <div class="rounded-2xl border border-white/10 bg-slate-950/50 backdrop-blur-xl px-5 py-4">
        <div class="flex items-center justify-between gap-4">
          <div>
            <h2 class="text-lg md:text-xl font-semibold text-slate-50">
              نمای کلی فروش حضوری
            </h2>
            <p class="mt-1 text-xs text-slate-400">
              خلاصه فروش خالص، مرجوعی، تعداد فاکتور و مقایسه امروز با دیروز             </p>
          </div>

          <div class="hidden md:flex items-center gap-2 text-[11px] text-cyan-100/70">
            <span class="w-2 h-2 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/50"></span>
            <span>POS Overview</span>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div class="rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/10 via-slate-900/80 to-slate-950 p-5 shadow-2xl shadow-cyan-500/10">
          <div class="flex items-center justify-between gap-4">
            <div>
              <div class="text-xs text-cyan-100/70 mb-2">
                فروش امروز
              </div>

              <div class="text-xl md:text-2xl font-black text-cyan-100 leading-tight">
                {{ overview.today_total | toMoney(0) }}
              </div>

              <div class="mt-2 text-xs text-cyan-100/60">
                {{ overview.today_orders_count || overview.today_orders || 0 }} فاکتور
                <span class="mx-1">/</span>
                {{ overview.today_items_quantity || 0 }} عدد کالا
              </div>

              <div class="mt-1 text-[11px] text-cyan-100/45" dir="ltr">
                روز شمسی تهران: {{ overview.today_jalali || '---' }}
              </div>

              <div
                  v-if="Number(overview.today_returned_amount || 0) > 0"
                  class="mt-2 rounded-xl border border-rose-400/20 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200"
              >
                کل مرجوعی:
                <b dir="ltr">{{ overview.today_returned_amount | toMoney(0) }}</b>
              </div>

              <div
                  v-if="Number(overview.today_direct_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                برگشت مستقیم: {{ overview.today_direct_returned_amount | toMoney(0) }}
              </div>

              <div
                  v-if="Number(overview.today_document_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                سند مرجوعی: {{ overview.today_document_returned_amount | toMoney(0) }}
              </div>

              <div class="mt-1 text-[11px] text-slate-500" dir="ltr">
                ناخالص: {{ overview.today_gross_total | toMoney(0) }}
              </div>
            </div>

            <div class="w-16 h-16 rounded-2xl border border-cyan-300/30 bg-cyan-400/10 flex items-center justify-center shadow-xl shadow-cyan-500/20">
              <svg class="w-8 h-8 text-cyan-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
              </svg>
            </div>
          </div>

          <div class="mt-4 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div class="h-full w-4/5 rounded-full bg-cyan-400/70"></div>
          </div>
        </div>

        <div class="rounded-3xl border border-indigo-400/20 bg-gradient-to-br from-indigo-500/10 via-slate-900/80 to-slate-950 p-5 shadow-2xl shadow-indigo-500/10">
          <div class="flex items-center justify-between gap-4">
            <div>
              <div class="text-xs text-indigo-100/70 mb-2">
                فروش دیروز
              </div>

              <div class="text-xl md:text-2xl font-black text-indigo-100 leading-tight">
                {{ overview.yesterday_total | toMoney(0) }}
              </div>

              <div class="mt-2 text-xs text-indigo-100/60">
                {{ overview.yesterday_orders_count || overview.yesterday_orders || 0 }} فاکتور
                <span class="mx-1">/</span>
                {{ overview.yesterday_items_quantity || 0 }} عدد کالا
              </div>

              <div class="mt-1 text-[11px] text-indigo-100/45" dir="ltr">
                روز شمسی تهران: {{ overview.yesterday_jalali || '---' }}
              </div>

              <div
                  v-if="Number(overview.yesterday_returned_amount || 0) > 0"
                  class="mt-2 rounded-xl border border-rose-400/20 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200"
              >
                کل مرجوعی:
                <b dir="ltr">{{ overview.yesterday_returned_amount | toMoney(0) }}</b>
              </div>

              <div
                  v-if="Number(overview.yesterday_direct_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                برگشت مستقیم: {{ overview.yesterday_direct_returned_amount | toMoney(0) }}
              </div>

              <div
                  v-if="Number(overview.yesterday_document_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                سند مرجوعی: {{ overview.yesterday_document_returned_amount | toMoney(0) }}
              </div>

              <div class="mt-1 text-[11px] text-slate-500" dir="ltr">
                ناخالص: {{ overview.yesterday_gross_total | toMoney(0) }}
              </div>
            </div>

            <div class="w-16 h-16 rounded-2xl border border-indigo-300/30 bg-indigo-400/10 flex items-center justify-center shadow-xl shadow-indigo-500/20">
              <svg class="w-8 h-8 text-indigo-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 17v-6a2 2 0 012-2h8M9 17H5a2 2 0 01-2-2V7a2 2 0 012-2h4m0 12l4-4m0 0l4 4m-4-4v8"/>
              </svg>
            </div>
          </div>

          <div class="mt-4 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div class="h-full w-3/5 rounded-full bg-indigo-400/70"></div>
          </div>
        </div>

        <div class="rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-500/10 via-slate-900/80 to-slate-950 p-5 shadow-2xl shadow-amber-500/10">
          <div class="flex items-center justify-between gap-4">
            <div>
              <div class="text-xs text-amber-100/70 mb-2">
                فروش این ماه
              </div>

              <div class="text-xl md:text-2xl font-black text-amber-100 leading-tight">
                {{ overview.month_total | toMoney(0) }}
              </div>

              <div class="mt-2 text-xs text-amber-100/60">
                {{ overview.month_orders_count || overview.month_orders || 0 }} فاکتور
                <span class="mx-1">/</span>
                {{ overview.month_items_quantity || 0 }} عدد کالا
              </div>

              <div
                  v-if="Number(overview.month_returned_amount || 0) > 0"
                  class="mt-2 rounded-xl border border-rose-400/20 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200"
              >
                کل مرجوعی:
                <b dir="ltr">{{ overview.month_returned_amount | toMoney(0) }}</b>
              </div>

              <div
                  v-if="Number(overview.month_direct_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                برگشت مستقیم: {{ overview.month_direct_returned_amount | toMoney(0) }}
              </div>

              <div
                  v-if="Number(overview.month_document_returned_amount || 0) > 0"
                  class="mt-1 text-[11px] text-orange-200"
                  dir="ltr"
              >
                سند مرجوعی: {{ overview.month_document_returned_amount | toMoney(0) }}
              </div>

              <div class="mt-1 text-[11px] text-slate-500" dir="ltr">
                ناخالص: {{ overview.month_gross_total | toMoney(0) }}
              </div>
            </div>

            <div class="w-16 h-16 rounded-2xl border border-amber-300/30 bg-amber-400/10 flex items-center justify-center shadow-xl shadow-amber-500/20">
              <svg class="w-8 h-8 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>

          <div class="mt-4 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div class="h-full w-5/6 rounded-full bg-amber-400/70"></div>
          </div>
        </div>

        <div class="rounded-3xl border border-purple-400/20 bg-gradient-to-br from-purple-500/10 via-slate-900/80 to-slate-950 p-5 shadow-2xl shadow-purple-500/10">
          <div class="flex items-center justify-between gap-4">
            <div>
              <div class="text-xs text-purple-100/70 mb-2">
                میانگین سبد
              </div>

              <div class="text-xl md:text-2xl font-black text-purple-100 leading-tight">
                {{ overview.avg_basket | toMoney(0) }}
              </div>

              <div class="mt-2 text-xs text-purple-100/60">
                میانگین ماه جاری
              </div>
            </div>

            <div class="w-16 h-16 rounded-2xl border border-purple-300/30 bg-purple-400/10 flex items-center justify-center shadow-xl shadow-purple-500/20">
              <svg class="w-8 h-8 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
          </div>

          <div class="mt-4 h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <div class="h-full w-2/3 rounded-full bg-purple-400/70"></div>
          </div>
        </div>
      </div>

      <!-- List -->
      <div v-if="false" class="w-full rounded-3xl border border-white/10 bg-slate-950/40 backdrop-blur-xl shadow-2xl shadow-black/30 overflow-hidden">
        <TransactionItemsList
            class="w-full min-w-0"
            title="لیست فروش"
            subtitle="جزئیات آیتم‌های فروش"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TransactionItemsList from "@/modules/partner/TransactionItemsList";

export default {
  name: 'PosOverviewTab',

  components: {
    TransactionItemsList,
  },

  props: {
    overview: {
      type: Object,
      default: () => ({
        today_total: 0,
        today_orders: 0,
        yesterday_total: 0,
        yesterday_orders: 0,
        month_total: 0,
        month_orders: 0,
        avg_basket: 0
      })
    }
  }
}
</script>